import { useState } from 'react'

import { useSelector } from 'react-redux'

import {
  BrandDoor,
  BrandShippingInfoDocument,
  OrderBilling,
  OrderBillingAddress,
  OrderBillingShippingDocument,
  OrderShippingAddress,
  OrderStatus,
  ShippingMethod,
  Tax,
} from '__generated__/atlas-types'

import { atlasClient } from 'graphql/client'
import { isEditApprovedShippedRestricted } from 'store/currentUser/selectors'

import { OrderData } from '../../Overview'
import { useOrderBillingShipping } from './addressesWidget.queries'
import { useUpdateAddresses } from './components/EditAddressesModal/hooks/useUpdateAddresses'
import { BillingAddressFormValues } from './components/Forms/BillingAddressForm/billingAddressForm.types'
import { formatShippingFormValuesToBillingFormValues } from './components/Forms/BillingAddressForm/billingAddressForm.utils'
import { useUpdateDoor } from './components/Forms/ShippingAddressForm/shippingAddressForm.hooks'
import { ShippingAddressFormValues } from './components/Forms/ShippingAddressForm/shippingAddressForm.types'
import { formatBillingFormValuesToShippingFormValues } from './components/Forms/ShippingAddressForm/shippingAddressForm.utils'
import { useOrderId } from 'features/Orders/orders.hooks'

const EMPTY_TRACKING_NUMBER = ' '

/**
 * useEditRestrict
 */
export function useEditRestrict(isBrand: boolean, orderStatus?: OrderStatus) {
  // TODO: test this in the UI
  const isRestrictedFromStatus = orderStatus
    ? [OrderStatus.APPROVED, OrderStatus.SHIPPED].includes(orderStatus)
    : true

  const isShippedAndRetailerLogged =
    orderStatus === OrderStatus.SHIPPED && !isBrand
  const isEditOfApprovedShippedRestricted =
    (useSelector(isEditApprovedShippedRestricted) && isRestrictedFromStatus) ||
    isShippedAndRetailerLogged

  return isEditOfApprovedShippedRestricted
}

/**
 * useAddressesWidgetData
 */
export type OrderBillingShippingData = {
  brandId?: string
  retailerId?: string
  orderBilling?: OrderBilling
  orderShippingAddress?: OrderShippingAddress
  orderBillingAddress?: OrderBillingAddress
  orderTax?: Tax | null
  orderStatus?: OrderStatus
  loadingAddressesWidgetData: boolean
  displayShippingCode?: boolean
  orderShippingMethod?: ShippingMethod | null
  door?: BrandDoor
  trackingNumber?: string | null
  paymentMethod?:
    | {
        id: string
        name?: string | null | undefined
        code?: string | null | undefined
      }
    | null
    | undefined
  isRetailerRestrictedFromEditing: boolean
  isOrderOrphan?: boolean
}

export const useAddressesWidgetData = (
  overviewData: {
    brand?: OrderData['brand']
    retailerProfile: OrderData['retailerProfile']
    orderStatus: OrderData['status']
    displayShippingCode: boolean
    isRetailerRestrictedFromEditing: boolean
    isOrderOrphan: boolean
  },
  loadingOverviewData: boolean,
): OrderBillingShippingData => {
  const { orderBillingShippingData, loading: loadingOrderBillingShippingData } =
    useOrderBillingShipping()

  return {
    brandId: overviewData?.brand?.id,
    retailerId: overviewData?.retailerProfile?.id,
    orderBilling: orderBillingShippingData?.billing,
    orderShippingAddress: orderBillingShippingData?.shipping.address,
    orderBillingAddress: orderBillingShippingData?.billing.address,
    orderTax: orderBillingShippingData?.tax,
    orderStatus: overviewData?.orderStatus || undefined,
    loadingAddressesWidgetData:
      loadingOrderBillingShippingData || loadingOverviewData, // we should return just one loading
    displayShippingCode:
      !!overviewData?.brand?.orderSettings?.displayShippingCode,
    orderShippingMethod: orderBillingShippingData?.shipping.method,
    door: orderBillingShippingData?.doors?.edges?.[0]?.node,
    trackingNumber: orderBillingShippingData?.trackingNumber,
    paymentMethod: orderBillingShippingData?.paymentMethod,
    isRetailerRestrictedFromEditing:
      Boolean(
        overviewData?.brand?.orderSettings?.retailerRestrictedFromEditing,
      ) ||
      Boolean(
        overviewData?.brand?.orderSettings?.restrictEditNotesAndReturnedOrders,
      ),
    isOrderOrphan: overviewData?.isOrderOrphan,
  }
}

/**
 * useModalHandlers
 */
type ActiveWarningModal =
  | 'warningUpdateOrder'
  | 'warningCompareAddress'
  | 'warningUpdateBothAddresses'
  | null

export const useModalHandlers = () => {
  const [isEditAddressModalOpen, setIsEditAddressModalOpen] = useState(false)

  const [activeWarningModal, setActiveWarningModal] =
    useState<ActiveWarningModal>(null)

  const openWarningModal = ({ which }: { which: ActiveWarningModal }) => {
    setActiveWarningModal(which)
  }

  const closeWarningModal = () => {
    setActiveWarningModal(null)
    setIsEditAddressModalOpen(true)
  }

  const closeAndResetModals = () => {
    setActiveWarningModal(null)
    setIsEditAddressModalOpen(false)
  }

  const openEditAddressModal = () => {
    setIsEditAddressModalOpen(true)
  }

  const closeEditAddressModal = () => {
    setIsEditAddressModalOpen(false)
  }

  return {
    openWarningModal,
    activeWarningModal,
    closeWarningModal,
    closeAndResetModals,
    isEditAddressModalOpen,
    openEditAddressModal,
    closeEditAddressModal,
  }
}

/**
 * useSubmitHandlers
 */
export type SubmitParams = {
  values: ShippingAddressFormValues | BillingAddressFormValues
  overwriteAddress: boolean
  changesMade: boolean
  initialDoor: BrandDoor | null | undefined
  initialTrackingNumber: string | null | undefined
  overrideSourceAddress?: boolean
}

type SubmitBillingParams = Omit<
  SubmitParams,
  'values' | 'initialDoor' | 'initialTrackingNumber' | 'from'
> & {
  values: BillingAddressFormValues
}

type SubmitShippingParams = Omit<SubmitParams, 'values' | 'from'> & {
  values: ShippingAddressFormValues
}

export type AddressesSubmitParams = {
  shipping?: SubmitShippingParams
  billing?: SubmitBillingParams
}

const getRefetchQueries = (overrideSourceAddress: boolean) => [
  OrderBillingShippingDocument,
  ...(overrideSourceAddress ? [BrandShippingInfoDocument] : []),
]

export const useSubmitHandlers = () => {
  const orderId = useOrderId()
  const {
    updateShippingAddress,
    updateBillingAddress,
    isUpdatingBillingAddress,
    isUpdatingShippingAddress,
  } = useUpdateAddresses()
  const { updateDoor, isUpdatingDoor } = useUpdateDoor()

  const submitShipping = async (params: SubmitShippingParams) => {
    const {
      values,
      overwriteAddress,
      changesMade,
      initialDoor,
      initialTrackingNumber,
      overrideSourceAddress,
    } = params

    const {
      trackingNumber: trackingNumberValue,
      door: doorOption,
      ...restValues
    } = values

    let doorInput = undefined

    if (typeof doorOption === 'string') {
      doorInput = doorOption
    } else if (!!doorOption && doorOption.value !== initialDoor?.id) {
      doorInput = doorOption.value
    }

    const hasUserDeletedTrackingNumber =
      trackingNumberValue === '' && initialTrackingNumber !== null

    // Our BE removes the tracking number if it's a blank space string > ' '
    // notice that once updated, it comes in the query as a blank space string
    const trackingNumberToSubmit = hasUserDeletedTrackingNumber
      ? EMPTY_TRACKING_NUMBER
      : trackingNumberValue

    const formValues = {
      ...restValues,
      ...(trackingNumberToSubmit &&
      trackingNumberToSubmit !== initialTrackingNumber
        ? { trackingNumber: trackingNumberToSubmit }
        : {}),
    }

    if (doorInput !== undefined) {
      await updateDoor(orderId, doorInput)
    }

    const promises = [
      ...(changesMade
        ? [updateShippingAddress(orderId, formValues, overrideSourceAddress)]
        : []),
      ...(overwriteAddress
        ? [
            updateBillingAddress(
              orderId,
              formatShippingFormValuesToBillingFormValues(formValues),
            ),
          ]
        : []),
    ]

    const include = getRefetchQueries(Boolean(overrideSourceAddress))

    Promise.all(promises).then(() => {
      atlasClient.refetchQueries({
        include,
      })
    })
  }

  const submitBilling = async (params: SubmitBillingParams) => {
    const { values, overwriteAddress, changesMade, overrideSourceAddress } =
      params

    const promises = [
      ...(changesMade
        ? [updateBillingAddress(orderId, values, overrideSourceAddress)]
        : []),
      ...(overwriteAddress
        ? [
            updateShippingAddress(
              orderId,
              formatBillingFormValuesToShippingFormValues(values),
            ),
          ]
        : []),
    ]

    const include = getRefetchQueries(Boolean(overrideSourceAddress))

    Promise.all(promises).then(() => {
      atlasClient.refetchQueries({
        include,
      })
    })
  }

  const submitAddress = async ({
    params,
    overrideSourceAddress = false,
  }: {
    params: AddressesSubmitParams
    overrideSourceAddress?: boolean
  }) => {
    const promises = [
      params.shipping &&
        submitShipping({
          ...params.shipping,
          overrideSourceAddress,
        }),
      params.billing &&
        submitBilling({
          ...params.billing,
          overrideSourceAddress,
        }),
    ].filter(Boolean)

    if (!promises.length) {
      return
    }

    if (params.shipping && params.billing) {
      return Promise.allSettled(promises)
    }
  }

  const submitStepAddresses = async ({
    params,
    overrideShipping = false,
    overrideBilling = false,
  }: {
    params: AddressesSubmitParams
    overrideShipping?: boolean
    overrideBilling?: boolean
  }) => {
    const promises = [
      params.shipping &&
        submitShipping({
          ...params.shipping,
          overrideSourceAddress: overrideShipping,
        }),
      params.billing &&
        submitBilling({
          ...params.billing,
          overrideSourceAddress: overrideBilling,
        }),
    ].filter(Boolean)

    if (!promises.length) {
      return
    }

    return Promise.allSettled(promises)
  }

  return {
    submitAddress,
    submitStepAddresses,
    isUpdating:
      isUpdatingBillingAddress || isUpdatingShippingAddress || isUpdatingDoor,
  }
}
